/**
 * PdfExportControls — sidebar building blocks shared by the PDF export pages
 * (TreatmentExportPage, AiSummaryPage): flat groups, toggle rows, text fields,
 * the 3-way signature segment and the two option groups every document has
 * (clinic letterhead, patient block). Toggle rows whose source value is
 * missing render disabled with a hint — toggles never invent data.
 *
 * Styles: pdfExportShell.css (pdx-*).
 */

import { Link } from "react-router-dom";
import { PenNib, Textbox, Prohibit, Gear } from "@phosphor-icons/react";

export const MISSING_CLINIC_HINT = "לא הוגדר בהגדרות הקליניקה";
export const MISSING_PATIENT_HINT = "לא הוזן בכרטיס המטופל";

export const hasValue = (v) => (typeof v === "string" ? v.trim().length > 0 : !!v);

export function ToggleRow({ label, checked, onChange, disabled, hint }) {
  return (
    <label className={`pdx-toggle-row ${disabled ? "disabled" : ""}`}>
      <span className="pdx-toggle-text">
        <span className="pdx-toggle-label">{label}</span>
        {hint && <span className="pdx-toggle-hint">{hint}</span>}
      </span>
      <span className="pdx-toggle">
        <input
          type="checkbox"
          checked={disabled ? false : checked}
          onChange={onChange}
          disabled={disabled}
        />
        <span className="pdx-toggle-slider" />
      </span>
    </label>
  );
}

export function Group({ title, action, children, className = "" }) {
  return (
    <section className={`pdx-group ${className}`}>
      <div className="pdx-group-head">
        <span className="pdx-group-title">{title}</span>
        {action}
      </div>
      {children}
    </section>
  );
}

export function TextField({
  id,
  label,
  value,
  onChange,
  max,
  multiline,
  placeholder,
  rows = 3,
  hint,
  disabled,
}) {
  const remaining = max - (value?.length || 0);
  return (
    <div className="pdx-field">
      <label htmlFor={id}>{label}</label>
      {multiline ? (
        <textarea
          id={id}
          value={value}
          rows={rows}
          maxLength={max}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          id={id}
          type="text"
          value={value}
          maxLength={max}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {hint && <span className="pdx-field-hint">{hint}</span>}
      {multiline && (
        <span className={`pdx-field-counter ${remaining < 50 ? "low" : ""}`}>
          {remaining} תווים נותרו
        </span>
      )}
    </div>
  );
}

const SIGNATURE_CHOICES = [
  { value: "saved", label: "חתימה שמורה", Icon: PenNib },
  { value: "box", label: "תיבה לחתימה ידנית", Icon: Textbox },
  { value: "none", label: "ללא", Icon: Prohibit },
];

export function SignatureSegment({ value, onChange, savedSignatureUrl, onDrawSignature }) {
  return (
    <>
      <div className="pdx-segmented" role="radiogroup" aria-label="סוג חתימה">
        {SIGNATURE_CHOICES.map(({ value: v, label, Icon }) => (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={value === v}
            className={`pdx-seg ${value === v ? "active" : ""}`}
            onClick={() => onChange(v)}
          >
            <Icon size={15} weight={value === v ? "fill" : "regular"} />
            {label}
          </button>
        ))}
      </div>
      {value === "saved" && (
        <div className="pdx-sig-row">
          {savedSignatureUrl ? (
            <>
              <img src={savedSignatureUrl} alt="החתימה השמורה שלך" className="pdx-sig-img" />
              <button type="button" className="pdx-link-btn" onClick={onDrawSignature}>
                החלפת חתימה
              </button>
            </>
          ) : (
            <>
              <span className="pdx-toggle-hint">אין חתימה שמורה עדיין.</span>
              <button type="button" className="pdx-link-btn" onClick={onDrawSignature}>
                חתימה ושמירה
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}

/**
 * Clinic letterhead toggles. `header` = options.header, `onToggle(key)`.
 * `authorLabel` lets the AI page say "שם המטפל/ת המפיק/ה".
 */
export function LetterheadToggles({ header, onToggle, clinic, hasLicense, isAdmin }) {
  const h = header;
  const settingsLink = isAdmin ? (
    <Link to="/dashboard/admin/settings" className="pdx-group-link">
      <Gear size={13} weight="bold" />
      הגדרות הקליניקה
    </Link>
  ) : null;
  const clinicRow = (key, label, field) => (
    <ToggleRow
      label={label}
      checked={h[key]}
      disabled={!hasValue(clinic?.[field])}
      hint={!hasValue(clinic?.[field]) ? MISSING_CLINIC_HINT : null}
      onChange={() => onToggle(key)}
    />
  );
  return (
    <Group title="כותרת עליונה — פרטי הקליניקה" action={settingsLink}>
      <div className="pdx-toggle-list">
        {clinicRow("logo", "לוגו", "logoUrl")}
        <ToggleRow
          label="לוגו גדול"
          checked={h.largeLogo}
          disabled={!hasValue(clinic?.logoUrl) || !h.logo}
          hint={!hasValue(clinic?.logoUrl) ? MISSING_CLINIC_HINT : !h.logo ? "הפעילו קודם את הלוגו" : null}
          onChange={() => onToggle("largeLogo")}
        />
        <ToggleRow label="שם הקליניקה" checked={h.clinicName} onChange={() => onToggle("clinicName")} />
        <ToggleRow label="שם המטפל/ת" checked={h.therapistName} onChange={() => onToggle("therapistName")} />
        {clinicRow("clinicAddress", "כתובת", "clinicAddress")}
        {clinicRow("contactPhone", "טלפון", "contactPhone")}
        {clinicRow("contactEmail", "אימייל", "contactEmail")}
        {clinicRow("website", "אתר", "website")}
        {clinicRow("businessId", "ח״פ / עוסק מורשה", "businessId")}
        <ToggleRow
          label="מספר רישיון"
          checked={h.license}
          disabled={!hasLicense}
          hint={!hasLicense ? "לא הוגדר בהגדרות / בפרופיל" : null}
          onChange={() => onToggle("license")}
        />
      </div>
    </Group>
  );
}

/**
 * Patient block toggles. `patient` = options.patient, `patientDoc` = the
 * patient record. `rows` hides rows that don't apply (e.g. no single
 * treatment date on the AI summary). `extra` renders additional rows at the
 * end of the list (same `ToggleRow`s).
 */
export function PatientToggles({
  patient,
  onToggle,
  patientDoc,
  typeName,
  rows = { treatmentDate: true, treatmentType: true },
  dateLabel = "תאריך ושעת הטיפול",
  extra = null,
}) {
  const p = patient;
  return (
    <Group title="פרטי המטופל/ת">
      <div className="pdx-toggle-list">
        <ToggleRow label="שם" checked={p.name} onChange={() => onToggle("name")} />
        {rows.treatmentDate && (
          <ToggleRow label={dateLabel} checked={p.treatmentDate} onChange={() => onToggle("treatmentDate")} />
        )}
        {rows.treatmentType && (
          <ToggleRow
            label="סוג הטיפול"
            checked={p.treatmentType}
            disabled={!hasValue(typeName)}
            onChange={() => onToggle("treatmentType")}
          />
        )}
        <ToggleRow
          label="תעודת זהות"
          checked={p.idNumber}
          disabled={!hasValue(patientDoc?.idNumber)}
          hint={!hasValue(patientDoc?.idNumber) ? MISSING_PATIENT_HINT : "מידע רגיש — כבוי כברירת מחדל"}
          onChange={() => onToggle("idNumber")}
        />
        <ToggleRow
          label="תאריך לידה וגיל"
          checked={p.birthDate}
          disabled={!patientDoc?.birthDate}
          hint={!patientDoc?.birthDate ? MISSING_PATIENT_HINT : null}
          onChange={() => onToggle("birthDate")}
        />
        <ToggleRow
          label="טלפון"
          checked={p.phone}
          disabled={!hasValue(patientDoc?.phone)}
          hint={!hasValue(patientDoc?.phone) ? MISSING_PATIENT_HINT : null}
          onChange={() => onToggle("phone")}
        />
        {extra}
      </div>
    </Group>
  );
}
